import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Calendar, ChevronLeft, ChevronRight, Check, X } from "lucide-react";

/**
 * Leave calendar — full-year grid with **Financial Year (Jul→Jun) /
 * Calendar Year (Jan→Dec) toggle**. Renders a 12-month strip with a
 * compact Mon-first day grid per month; clicking any day reveals the
 * approved/pending leave entries for that day.
 *
 * Backed by `GET /api/integrations/keypay/clients/{cid}/leave-calendar`
 * which now fetches the entire `/leaverequest` feed (KeyPay's
 * fromDate/toDate query params return HTTP 500 if supplied) and
 * filters client-side.
 */
export default function KeyPayLeaveCalendarPage() {
  const { clientId } = useParams();
  const [mode, setMode] = useState(() => localStorage.getItem("kp_leave_mode") || "fy"); // "fy" | "cy"
  // For FY mode `anchor` is the start year (e.g. 2025 == FY25/26).
  // For CY mode `anchor` is the calendar year.
  const today = new Date();
  const [anchor, setAnchor] = useState(() => {
    const m = today.getMonth(); // 0-Jan, 6-Jul
    return m >= 6 ? today.getFullYear() : today.getFullYear() - 1;
  });
  const [anchorCy, setAnchorCy] = useState(today.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState("all"); // "all" | <employee_id>

  // Build the dropdown options from whoever has leave in the loaded
  // window. Sorted A→Z by employee name for predictable scanning.
  const employeeOptions = useMemo(() => {
    const seen = new Map();
    for (const r of data?.leave || []) {
      if (r.employee_id == null) continue;
      if (!seen.has(r.employee_id)) {
        seen.set(r.employee_id, {
          id: String(r.employee_id),
          name: r.employee_name || `Employee ${r.employee_id}`,
        });
      }
    }
    return Array.from(seen.values())
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Apply the dropdown filter — when "all" the entire leave list is
  // used, otherwise only rows for the selected employee_id.
  const filteredLeave = useMemo(() => {
    const rows = data?.leave || [];
    if (employeeFilter === "all") return rows;
    return rows.filter((r) => String(r.employee_id) === employeeFilter);
  }, [data, employeeFilter]);

  const { fromIso, toIso, label, months } = useMemo(() => {
    if (mode === "fy") {
      const fromIso = `${anchor}-07-01`;
      const toIso = `${anchor + 1}-06-30`;
      const label = `FY${String(anchor).slice(2)}/${String(anchor + 1).slice(2)}`;
      const months = [];
      for (let i = 0; i < 12; i++) {
        const yr = i < 6 ? anchor : anchor + 1;
        const mo = (6 + i) % 12;
        months.push({ year: yr, month: mo });
      }
      return { fromIso, toIso, label, months };
    }
    const fromIso = `${anchorCy}-01-01`;
    const toIso = `${anchorCy}-12-31`;
    return {
      fromIso, toIso, label: String(anchorCy),
      months: Array.from({ length: 12 }, (_, i) => ({ year: anchorCy, month: i })),
    };
  }, [mode, anchor, anchorCy]);

  useEffect(() => { localStorage.setItem("kp_leave_mode", mode); }, [mode]);

  const load = useCallback(() => {
    setLoading(true);
    setSelectedDay(null);
    return api.get(`/integrations/keypay/clients/${clientId}/leave-calendar`,
                   { params: { from_date: fromIso, to_date: toIso } })
      .then((r) => { setData(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load leave"))
      .finally(() => { setLoading(false); });
  }, [clientId, fromIso, toIso]);

  useEffect(() => { load(); }, [load]);

  // Build day → leave-rows index for instant lookup on click. Uses
  // `filteredLeave` so the day cells, colours, and selected-day list
  // all respect the employee dropdown.
  const byDay = useMemo(() => {
    const m = {};
    for (const lv of filteredLeave) {
      const start = lv.from ? new Date(lv.from) : null;
      const end = lv.to ? new Date(lv.to) : start;
      if (!start) continue;
      const cur = new Date(start);
      while (cur <= end) {
        const k = iso(cur);
        (m[k] = m[k] || []).push(lv);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [filteredLeave]);

  const moveAnchor = (dir) => {
    if (mode === "fy") setAnchor((a) => a + dir);
    else setAnchorCy((a) => a + dir);
  };

  const selectedRows = selectedDay ? (byDay[selectedDay] || []) : [];

  return (
    <div className="p-6 max-w-7xl" data-testid="keypay-leave-calendar">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-leave-back">
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>

      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100 text-amber-700">
          <Calendar className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">KEYPAY · LEAVE · {mode === "fy" ? "FINANCIAL YEAR" : "CALENDAR YEAR"}</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            Leave · {label}
          </h1>
          {data && (
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
              {employeeFilter === "all" ? (
                <>
                  {data.count} entr{data.count === 1 ? "y" : "ies"} in window
                  {data.approved_count > 0 && <span className="text-emerald-700"> · {data.approved_count} approved</span>}
                  {data.pending_count > 0 && <span className="text-amber-700"> · {data.pending_count} pending</span>}
                  {data.rejected_count > 0 && <span className="text-red-700"> · {data.rejected_count} rejected</span>}
                  <span className="text-zinc-400"> · {data.total_count_all_time} all-time</span>
                </>
              ) : (
                <>
                  <span className="text-zinc-900 font-bold">
                    {filteredLeave.length} entr{filteredLeave.length === 1 ? "y" : "ies"}
                  </span>
                  {" "}for {employeeOptions.find((o) => o.id === employeeFilter)?.name || "selected employee"}
                  <span className="text-zinc-400"> · {data.count} across all staff in window</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Employee filter dropdown — defaults to "All employees" */}
        <Select value={employeeFilter} onValueChange={(v) => { setEmployeeFilter(v); setSelectedDay(null); }}>
          <SelectTrigger
            className="rounded-sm h-9 w-[220px]"
            data-testid="keypay-leave-employee-select">
            <SelectValue placeholder="All employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="keypay-leave-employee-all">
              All employees
              {employeeOptions.length > 0 && (
                <span className="text-zinc-400 font-mono ml-1">· {employeeOptions.length}</span>
              )}
            </SelectItem>
            {employeeOptions.map((emp) => (
              <SelectItem key={emp.id} value={emp.id}
                data-testid={`keypay-leave-employee-${emp.id}`}>
                {emp.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* FY / CY toggle */}
        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden"
          data-testid="keypay-leave-mode-toggle">
          <button
            onClick={() => setMode("fy")}
            data-testid="keypay-leave-mode-fy"
            className={`px-3 h-9 font-display font-bold text-xs uppercase tracking-wider transition ${
              mode === "fy" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
            }`}>
            Financial year
          </button>
          <button
            onClick={() => setMode("cy")}
            data-testid="keypay-leave-mode-cy"
            className={`px-3 h-9 font-display font-bold text-xs uppercase tracking-wider transition border-l border-zinc-200 ${
              mode === "cy" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
            }`}>
            Calendar year
          </button>
        </div>

        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={() => moveAnchor(-1)}
          data-testid="keypay-leave-prev">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={() => moveAnchor(1)}
          data-testid="keypay-leave-next">
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></CardContent></Card>
      ) : (
        <>
          <Card className="rounded-sm border-zinc-200 mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
                data-testid="keypay-leave-grid">
                {months.map(({ year, month }) => (
                  <MiniMonth key={`${year}-${month}`}
                    year={year} month={month}
                    byDay={byDay}
                    selectedDay={selectedDay}
                    onSelect={setSelectedDay} />
                ))}
              </div>

              {/* Legend */}
              <div className="mt-4 flex items-center gap-3 text-[10px] font-mono text-zinc-500">
                <Swatch className="bg-emerald-200" label="Approved" />
                <Swatch className="bg-amber-200" label="Pending" />
                <Swatch className="bg-red-200" label="Rejected" />
                <span className="ml-auto text-zinc-400">Click a coloured day to see who&apos;s on leave.</span>
              </div>
            </CardContent>
          </Card>

          {/* Selected-day details */}
          {selectedDay && (
            <Card className="rounded-sm border-zinc-200" data-testid="keypay-leave-selected">
              <CardContent className="p-0">
                <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center justify-between">
                  <span>Leave on {selectedDay}</span>
                  <button onClick={() => setSelectedDay(null)} className="text-zinc-400 hover:text-zinc-900 normal-case tracking-normal">Clear</button>
                </div>
                {selectedRows.length === 0 ? (
                  <div className="p-6 text-center text-sm italic text-zinc-500">No leave recorded for this day.</div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {selectedRows.map((r) => (
                      <div key={r.id}
                        className="grid grid-cols-[1fr_160px_120px_90px_80px_150px] gap-3 px-4 py-2 items-center text-sm hover:bg-zinc-50">
                        <Link to={`/integrations/keypay/clients/${clientId}/employees/${r.employee_id}`}
                          className="truncate font-semibold hover:underline">{r.employee_name}</Link>
                        <span className="text-xs text-zinc-600 truncate">{r.leave_category}</span>
                        <span className="text-[11px] font-mono text-zinc-500">
                          {r.from === r.to ? r.from : `${r.from} → ${r.to}`}
                        </span>
                        <Badge className={`rounded-sm text-[10px] justify-self-start ${
                          r.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : r.status === "pending" ? "bg-amber-100 text-amber-800 border-amber-200"
                          : "bg-zinc-100 text-zinc-700"}`}>
                          {r.status}
                        </Badge>
                        <span className="font-mono text-xs text-right">{r.hours.toFixed(1)}h</span>
                        <LeaveActionButtons row={r} clientId={clientId} onDone={load} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function MiniMonth({ year, month, byDay, selectedDay, onSelect }) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = (first.getDay() + 6) % 7; // Mon-first
  const todayIso = iso(new Date());

  const cells = [];
  for (let i = 0; i < startDow; i++) {
    // Spacer cells for days that fall in the previous month — give
    // them stable ids derived from the actual calendar date so React
    // can keep DOM nodes between renders.
    const dt = new Date(year, month, 1 - (startDow - i));
    cells.push({ d: null, k: `spacer-${iso(dt)}` });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    const dt = new Date(year, month, d);
    const k = iso(dt);
    const rows = byDay[k] || [];
    // Worst-case status: rejected > pending > approved.
    let status = null;
    for (const r of rows) {
      if (r.status === "rejected") { status = "rejected"; break; }
      if (r.status === "pending") status = "pending";
      else if (!status && r.status === "approved") status = "approved";
    }
    cells.push({ d, k, status, count: rows.length, isToday: k === todayIso });
  }

  return (
    <div className="border border-zinc-200 rounded-sm p-2">
      <div className="text-[11px] font-display font-bold uppercase tracking-wider mb-1.5 text-zinc-700">
        {first.toLocaleString(undefined, { month: "short" })} <span className="text-zinc-400 font-mono">{year}</span>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[8px] font-mono text-zinc-400 mb-0.5">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <span key={d} className="text-center">{d.charAt(0)}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((c) => !c.d ? (
          <span key={c.k} />
        ) : (
          <button key={c.k}
            onClick={() => c.status && onSelect(c.k)}
            disabled={!c.status}
            data-testid={`keypay-leave-day-${c.k}`}
            title={c.status ? `${c.count} leave entr${c.count === 1 ? "y" : "ies"}` : ""}
            className={`text-[9px] h-6 rounded-sm flex items-center justify-center font-mono transition ${
              c.status === "approved" ? "bg-emerald-200 text-emerald-900 hover:bg-emerald-300 cursor-pointer"
              : c.status === "pending" ? "bg-amber-200 text-amber-900 hover:bg-amber-300 cursor-pointer"
              : c.status === "rejected" ? "bg-red-200 text-red-900 hover:bg-red-300 cursor-pointer"
              : "text-zinc-500"
            } ${c.isToday ? "ring-1 ring-zinc-900" : ""} ${
              selectedDay === c.k ? "outline outline-2 outline-zinc-900" : ""
            }`}>
            {c.d}
          </button>
        ))}
      </div>
    </div>
  );
}

function Swatch({ className, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-3 w-3 rounded-sm ${className}`} /> {label}
    </span>
  );
}
function iso(d) { return d.toISOString().slice(0, 10); }

function LeaveActionButtons({ row, clientId, onDone }) {
  const [busy, setBusy] = useState(null); // "approve" | "decline" | null
  if (row.status !== "pending") return <span />;
  const call = async (action) => {
    setBusy(action);
    try {
      let body = null;
      if (action === "decline") {
        const reason = window.prompt("Decline reason (optional):", "");
        if (reason === null) { setBusy(null); return; }
        body = { reason };
      }
      await api.post(`/integrations/keypay/clients/${clientId}/leave-requests/${row.id}/${action}`,
                     body || {});
      toast.success(`Leave ${action}d`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally { setBusy(null); }
  };
  return (
    <span className="flex gap-1 justify-end">
      <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700"
        onClick={() => call("approve")} disabled={!!busy}
        data-testid={`keypay-leave-approve-${row.id}`}>
        {busy === "approve" ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <><Check className="h-3 w-3 mr-0.5" />Approve</>}
      </Button>
      <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-red-200 text-red-700 hover:bg-red-50"
        onClick={() => call("decline")} disabled={!!busy}
        data-testid={`keypay-leave-decline-${row.id}`}>
        {busy === "decline" ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <><X className="h-3 w-3 mr-0.5" />Decline</>}
      </Button>
    </span>
  );
}
